import { useEffect } from 'react';
import { useLocation, useNavigate, useRouteError } from 'react-router-dom';

import AccessDenied from '@/app/ui/AccessDenied/AccessDenied';
import { ROUTES } from '@/config/routes';
import { getErrorKind } from '@/shared/lib/errors';
import { FullPageLoader } from '@/shared/ui/Loading';
import NotFound from '@/shared/ui/NotFound';

import AppCrashPage from './AppCrashPage';

interface Props {
  // Page-level boundaries render inside the shell; the root one replaces it.
  fullScreen?: boolean;
}

function RouteErrorBoundary({ fullScreen = false }: Props) {
  const error = useRouteError();
  const navigate = useNavigate();
  const { pathname, search, hash, state } = useLocation();

  useEffect(() => {
    console.error('🔴 RouteErrorBoundary caught:', error);
  }, [error]);

  const kind = getErrorKind(error);

  if (kind === 'notFound') return <NotFound />;
  if (kind === 'forbidden') return <AccessDenied />;
  // The global 401 handler is already redirecting to the login page.
  if (kind === 'unauthorized') return <FullPageLoader />;

  return (
    <AppCrashPage
      error={error}
      fullScreen={fullScreen}
      // Re-navigating to the same URL gives the router a new location, which
      // clears its error state and re-mounts the page.
      onRetry={() => navigate({ pathname, search, hash }, { replace: true, state })}
      onGoHome={() => navigate(ROUTES.AUTHORIZED.LANDING)}
    />
  );
}

export default RouteErrorBoundary;
